/**
 * O pote de estrelas (`starlight_jar`) do Flutter: `star_note.dart`,
 * `starlight_service.dart` e a matemática do `_JarStarFieldPainter` de
 * `starlight_jar_widget.dart`.
 *
 * Mora aqui o que as duas telas de Lembranças e as duas prévias do painel
 * precisam dizer igual: o formato do documento, a tabela de categorias, as
 * contas do serviço (a ordem, o "neste dia", o sorteio, a busca) e o voo de
 * cada estrela dentro do vidro. A tabela de categorias já estava copiada nas
 * duas telas; a prévia seria a terceira cópia, e três cópias de uma tabela é
 * como uma delas fica para trás.
 *
 * Fica de fora o que é mesmo de cada aparelho: as cinco cores das estrelas (o
 * site tem `var(--rosa-quartzo)`, o celular `cor.rosaQuartzo`) e o jeito de
 * pintar — lá é um `Canvas`, aqui é um `<canvas>` no navegador e um `<Svg>` no
 * celular. O que atravessa é o número.
 *
 * As chaves são as do `starCategories` e são gravadas cruas: uma "Gratidão" em
 * português no campo `category` seria uma estrela que o outro app não filtra.
 */

import { Acaso, semear } from '../academia/acaso';
import { lerData } from '../nucleo/tempo';
import type { Documento } from '../types/dados';
import type { NomeDoIcone } from '../theme/desenhos';

/** O documento cru, como o `StarNote.toMap` o grava. */
export interface DocEstrela {
  content?: string;
  author?: string;
  timestamp?: string;
  category?: string;
  tags?: string[];
  monthDay?: string;
}

/** Um bilhete guardado, do jeito que o armazém entrega. */
export type Estrela = Documento<DocEstrela>;

/**
 * O `starCategoryInfo`, na ordem do `starCategories`. Lá cada uma tem um emoji;
 * aqui são os desenhos da casa, que é a regra desta parte do app — o que o
 * bilhete preserva é o lugar, o tamanho e a cor deles, não o traço do emoji.
 */
export const CATEGORIAS_DA_ESTRELA: ReadonlyArray<{
  chave: string;
  nome: string;
  icone: NomeDoIcone;
}> = [
  { chave: 'gratitude', nome: 'Gratidão', icone: 'maos' },
  { chave: 'memory', nome: 'Memória', icone: 'camera' },
  { chave: 'love', nome: 'Amor', icone: 'coracao' },
  { chave: 'dream', nome: 'Sonho', icone: 'lua' },
  { chave: 'milestone', nome: 'Marco', icone: 'marco' },
  { chave: 'surprise', nome: 'Surpresa', icone: 'brilho' },
];

const PELA_CHAVE = new Map(CATEGORIAS_DA_ESTRELA.map((c) => [c.chave, c]));

/** O `category = 'gratitude'` que o construtor do `StarNote` assume. */
const GRATIDAO = CATEGORIAS_DA_ESTRELA[0];

/** A categoria de um bilhete, com `Gratidão` para quem não trouxe nenhuma. */
export function categoriaDaEstrela(chave: string | undefined) {
  return (chave ? PELA_CHAVE.get(chave) : undefined) ?? GRATIDAO;
}

/** Quantos bilhetes o `getStarNotes` entrega. */
export const TETO_DO_POTE = 40;

/** De quantos o `getRandomStarNote` sorteia. */
export const TETO_DO_SORTEIO = 20;

/** Quantos o `searchStars` devolve. */
export const TETO_DA_BUSCA = 50;

/** Um bilhete sem data legível conta como agora, para a parede não o derrubar. */
function quando(x: Estrela): number {
  return lerData(x.dados.timestamp)?.getTime() ?? Date.now();
}

/** O `_ordenadas`: do mais novo para o mais velho. */
export function ordenarEstrelas(estrelas: ReadonlyArray<Estrela>): Estrela[] {
  return [...estrelas].sort((a, b) => quando(b) - quando(a));
}

/** O `getStarsFromThisDay`: mesmo dia e mês, de outro ano. */
export function estrelasDesteDia(
  estrelas: ReadonlyArray<Estrela>,
  hoje: Date = new Date(),
): Estrela[] {
  return estrelas.filter((x) => {
    const d = lerData(x.dados.timestamp);
    return (
      d !== null &&
      d.getMonth() === hoje.getMonth() &&
      d.getDate() === hoje.getDate() &&
      d.getFullYear() !== hoje.getFullYear()
    );
  });
}

/**
 * O `_filterNotes`: a busca casa com o texto ou com qualquer marca, e a
 * categoria vem depois. Busca vazia e categoria nula devolvem tudo.
 */
export function filtrarEstrelas(
  estrelas: ReadonlyArray<Estrela>,
  busca: string,
  categoria: string | null,
): Estrela[] {
  const alvo = busca.trim().toLowerCase();
  return estrelas.filter((x) => {
    if (alvo) {
      const texto = (x.dados.content ?? '').toLowerCase();
      const marcas = x.dados.tags ?? [];
      if (!texto.includes(alvo) && !marcas.some((t) => t.toLowerCase().includes(alvo))) {
        return false;
      }
    }
    if (categoria && (x.dados.category ?? GRATIDAO.chave) !== categoria) return false;
    return true;
  });
}

/**
 * O `getRandomStarNote`: embaralha os vinte mais novos e pega o primeiro.
 *
 * Aqui é um sorteio só, porque `shuffle().first` e "um índice qualquer" dão a
 * mesma distribuição — e este é o sorteio do momento, que ninguém precisa
 * reproduzir do outro lado.
 */
export function sortearEstrela(estrelas: ReadonlyArray<Estrela>): Estrela | null {
  const recentes = ordenarEstrelas(estrelas).slice(0, TETO_DO_SORTEIO);
  if (recentes.length === 0) return null;
  return recentes[Math.floor(Math.random() * recentes.length)];
}

/**
 * A data do bilhete aberto: `M/D/AAAA`, sem zero na frente.
 *
 * É o `"${timestamp.month}/${timestamp.day}/${timestamp.year}"` do
 * `note_display_dialog.dart`, escrito à mão lá e por isso à mão aqui — nenhum
 * `Intl` desta casa formata assim.
 */
export function dataDoBilhete(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

/**
 * A caixa em que as estrelas voam, do `_JarStarFieldPainter`.
 *
 * O vidro desenhado começa em 20 e termina em 370; esta caixa vai de 56 a 406.
 * Os números são os de lá, copiados: a estrela mais baixa encosta um pouco
 * abaixo da base do pote, e é assim que a tela do Flutter aparece.
 */
export const POTE = {
  esquerda: 60,
  topo: 56,
  direita: 340,
  fundo: 406,
  /** O lado da maior estrela, que é o quanto o canto de cima recua do fim. */
  maior: 24,
  /** O `SizedBox(width: 400)` dentro do `SizedBox(height: 480)`. */
  largura: 400,
  altura: 480,
} as const;

/** O que cada estrela tem de seu, sorteado uma vez e guardado. */
export interface MovimentoDaEstrela {
  baseX: number;
  baseY: number;
  ampX: number;
  ampY: number;
  velX: number;
  velY: number;
  faseX: number;
  faseY: number;
  velGiro: number;
  faseDoPisca: number;
  giroBase: number;
  /** O índice na lista de cinco cores que cada aparelho tem a sua. */
  cor: number;
}

const DOIS_PI = Math.PI * 2;

/**
 * O `_StarMotion.fromSeed`, sorteio por sorteio, com o `Random` do Dart que já
 * mora em `academia/acaso`.
 *
 * A semente muda: lá é `note.id.hashCode`, que não é o mesmo número em dois
 * aparelhos, e por isso não serve. Aqui é o `semear` da casa. A sequência é
 * outra, a propriedade é a mesma — o mesmo bilhete tem sempre a mesma estrela,
 * no mesmo canto do vidro, em qualquer tela.
 */
export function movimentoDaEstrela(id: string): MovimentoDaEstrela {
  const r = new Acaso(semear(id));
  return {
    baseX: 100 + r.proximoDouble() * 160,
    baseY: 100 + r.proximoDouble() * 200,
    ampX: 8 + r.proximoDouble() * 12,
    ampY: 6 + r.proximoDouble() * 10,
    velX: 0.55 + r.proximoDouble() * 0.45,
    velY: 0.4 + r.proximoDouble() * 0.4,
    faseX: r.proximoDouble() * DOIS_PI,
    faseY: r.proximoDouble() * DOIS_PI,
    velGiro: 0.25 + r.proximoDouble() * 0.55,
    faseDoPisca: r.proximoDouble() * DOIS_PI,
    giroBase: r.proximoDouble() * DOIS_PI,
    cor: r.proximo(5),
  };
}

/** Onde a estrela está no instante `t` da volta de catorze segundos. */
export interface PousoDaEstrela {
  x: number;
  y: number;
  giro: number;
  opacidade: number;
  escala: number;
}

const prender = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * O corpo do `paint`: duas senoides somadas em cada eixo, o giro lento, o
 * pisca-pisca e a respiração do tamanho.
 *
 * A cor guardada no `_StarMotion` está a 70%, e lá isso não chega à tela: o
 * `withValues(alpha:)` do Flutter *troca* o canal em vez de multiplicá-lo, e o
 * pintor aplica o `opacity` do pisca por cima. Quem manda é este número, e é
 * ele que sai daqui.
 */
export function pousoDaEstrela(m: MovimentoDaEstrela, t: number): PousoDaEstrela {
  const tX = t * m.velX;
  const tY = t * m.velY;

  const cruX =
    m.baseX +
    Math.sin(tX * DOIS_PI + m.faseX) * m.ampX +
    Math.sin(tX * DOIS_PI * 0.37 + m.faseX * 1.7) * m.ampX * 0.3;
  const cruY =
    m.baseY +
    Math.cos(tY * DOIS_PI + m.faseY) * m.ampY +
    Math.cos(tY * DOIS_PI * 0.43 + m.faseY * 1.3) * m.ampY * 0.25;

  return {
    x: prender(cruX, POTE.esquerda, POTE.direita - POTE.maior),
    y: prender(cruY, POTE.topo, POTE.fundo - POTE.maior),
    giro: m.giroBase + Math.sin(t * DOIS_PI * m.velGiro) * 0.5,
    opacidade: prender(0.55 + Math.sin(t * DOIS_PI * 2.3 + m.faseDoPisca) * 0.35, 0, 1),
    escala: 0.85 + Math.sin(t * DOIS_PI * 1.2 + m.faseX) * 0.15,
  };
}

/**
 * O `_starPath`: dez vértices, alternando o raio cheio e o de 22%, começando em
 * cima. Os ângulos não mudam nunca, então a estrela de raio 1 é feita uma vez e
 * o tamanho é só uma multiplicação — o painel repinta isto quarenta vezes por
 * quadro.
 */
const ESTRELA_DE_UM: ReadonlyArray<{ x: number; y: number }> = Array.from(
  { length: 10 },
  (_, i) => {
    const raio = i % 2 === 0 ? 0.5 : 0.22;
    const angulo = -Math.PI / 2 + (i * Math.PI) / 5;
    return { x: Math.cos(angulo) * raio, y: Math.sin(angulo) * raio };
  },
);

export function pontosDaEstrela(tamanho: number): Array<{ x: number; y: number }> {
  return ESTRELA_DE_UM.map((p) => ({ x: p.x * tamanho, y: p.y * tamanho }));
}
